// /src/services/tracking.ts
// Centraliza integração com Google Tag Manager e Meta Pixel (fbq + gtag).
// Compatível com import ESM e injeção global (window.Tracking).

import {
    GOOGLE_ADS_ID,
    GOOGLE_ADS_INITIATE_CHECKOUT_LABEL,
    GOOGLE_ADS_PURCHASE_LABEL,
    META_PIXEL_ID,
    TRACKING_ENABLED,
} from '@/config/env'

export type CurrencyCode = 'BRL' | 'USD' | 'EUR' | string;

export interface PurchaseOptions {
    value?: number;
    currency?: CurrencyCode;
    transactionId?: string;
    productId?: string;
    prazo?: string;
    planName?: string;
}

export interface BotEventHandler {
    (payload: any, eventId: string): void;
}

export interface TrackingAPI {
    // Eventos unificados (Google + Meta)
    trackInitiateCheckout: (url?: string) => boolean;
    trackPurchase: (options?: PurchaseOptions) => boolean;

    // Eventos Google Ads
    trackGoogleInitiateCheckout: (url?: string) => boolean;
    trackGooglePurchase: (options?: PurchaseOptions) => boolean;

    // Eventos Meta Pixel
    trackMetaInitiateCheckout: () => void;
    trackMetaPurchase: (options?: PurchaseOptions) => void;

    // Integração com o bot
    handleBotEvent: (eventId: unknown, payload?: any) => boolean;
    onBotEvent: (eventId: unknown, handler: BotEventHandler) => boolean;
    offBotEvent: (eventId: unknown) => boolean;

    __initialized: boolean;
}

const noop = () => {
};
const noopFalse = () => false;

const NOOP_TRACKING: TrackingAPI = {
    trackInitiateCheckout: noopFalse,
    trackPurchase: noopFalse,
    trackGoogleInitiateCheckout: noopFalse,
    trackGooglePurchase: noopFalse,
    trackMetaInitiateCheckout: noop,
    trackMetaPurchase: noop,
    handleBotEvent: noopFalse,
    onBotEvent: noopFalse,
    offBotEvent: noopFalse,
    __initialized: true,
};

// --- IMPLEMENTAÇÃO ---
(() => {
    if (typeof window === 'undefined') {
        return;
    }
    if ((window as any).Tracking && (window as any).Tracking.__initialized) {
        return;
    }

    if (!TRACKING_ENABLED) {
        (window as any).Tracking = NOOP_TRACKING;
        return;
    }

    /** Identificadores GTM / Meta Pixel **/
    const hasGoogleAdsId = Boolean(GOOGLE_ADS_ID);
    const hasMetaPixelId = Boolean(META_PIXEL_ID);
    const GOOGLE_CONVERSIONS = {
        initiateCheckout: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_INITIATE_CHECKOUT_LABEL}`,
        purchase: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
    } as const;

    /** =======================
     *  CARREGAMENTO DO META PIXEL (fbq)
     *  - Injeta o script do Meta Pixel
     *  - Inicializa fbq e dispara PageView
     *  - Protege contra bloqueadores (try/catch)
     *  ======================= */
    try {
        if (!hasMetaPixelId) {
            console.debug('⚠️ Meta Pixel ID não configurado; tracking do Meta desativado.');
        } else if (!(window as any).fbq) {
            // Implementação do Meta Pixel baseada na documentação oficial
            (function (f: any, b: any, e: string, v: string) {
                if (f.fbq) {
                    return;
                }
                const n: any = f.fbq = function () {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                };
                if (!f._fbq) {
                    f._fbq = n;
                }
                n.push = n;
                n.loaded = true;
                n.version = '2.0';
                n.queue = [];
                const t = b.createElement(e);
                t.async = true;
                t.src = v;
                const s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
                t.onerror = () => {
                    console.debug('❌ Meta Pixel script bloqueado ou indisponível:', v);
                };
            })(
                window,
                document,
                'script',
                'https://connect.facebook.net/en_US/fbevents.js'
            );

            // Inicializa o Meta Pixel com o ID
            (window as any).fbq('init', META_PIXEL_ID);

            // Dispara evento PageView inicial
            (window as any).fbq('track', 'PageView');

            console.debug('✅ Meta Pixel inicializado:', META_PIXEL_ID);
        }
    } catch (err) {
        console.debug('⚠️ Meta Pixel bloqueado por extensão ou política de privacidade:', err);
    }

    /** =======================
     *  CARREGAMENTO SEGURO DO GTM (gtag.js)
     *  - Injeta o script apenas uma vez
     *  - Inicializa dataLayer/gtag se possível
     *  - Protege contra bloqueadores (try/catch)
     *  ======================= */
    try {
        if (!hasGoogleAdsId) {
            console.debug('⚠️ Google Ads ID não configurado; tracking do Google desativado.');
        } else {
            // Não injetar se já existe um script com esse src
            const selector = `script[src*="googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}"]`;
            if (!document.querySelector(selector)) {
                const script = document.createElement('script');
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
                script.onerror = () => {
                    console.debug('❌ Google Ads script bloqueado ou indisponível:', script.src);
                };
                document.head.appendChild(script);
            }
        }

        // Inicializa dataLayer se necessário
        (window as any).dataLayer = (window as any).dataLayer || [];

        // Define window.gtag de forma segura – se já existir, não sobrescreve
        if (typeof (window as any).gtag !== 'function') {
            (window as any).gtag = function gtag() {
                // empurra os argumentos para dataLayer (compatível com GTM)
                (window as any).dataLayer.push(arguments);
            };
        }

        // Configura gtag se disponível (pode ser bloqueado por extensão, handle silencioso)
        if (hasGoogleAdsId) {
            try {
                (window as any).gtag('js', new Date());
                (window as any).gtag('config', GOOGLE_ADS_ID);
                console.debug('✅ Google Ads inicializado:', GOOGLE_ADS_ID);
            } catch (e) {
                // se gtag real estiver bloqueado, isso falhará; apenas log debug
                console.debug('gtag config falhou (provavelmente bloqueado):', e);
            }
        }
    } catch (err) {
        console.debug('⚠️ GTM bloqueado por extensão ou política de privacidade:', err);
    }

    /** =======================
     *  GOOGLE TAG MANAGER
     *  ======================= */
    function sendGoogleConversion(
        key: keyof typeof GOOGLE_CONVERSIONS,
        extra: Record<string, any> = {},
        redirectUrl?: string
    ): boolean {
        if (!hasGoogleAdsId) {
            console.debug('⚠️ Google Ads ID não configurado para conversão:', key);
            return false;
        }
        const sendTo = GOOGLE_CONVERSIONS[key];
        const callback = () => {
            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
        };

        if (typeof (window as any).gtag === 'function') {
            try {
                (window as any).gtag('event', 'conversion', {
                    send_to: sendTo,
                    event_callback: callback,
                    ...extra,
                });
                console.debug('✅ Google Conversion enviado:', key, extra);
                return true;
            } catch (err) {
                console.debug('❌ sendGoogleConversion falhou:', err);
                return false;
            }
        }
        console.debug('⚠️ gtag não disponível para:', key);
        return false;
    }

    function trackGoogleInitiateCheckout(url?: string) {
        return sendGoogleConversion('initiateCheckout', {}, url);
    }

    function trackGooglePurchase(options?: PurchaseOptions) {
        const extra: Record<string, any> = {
            value: options?.value ?? 1.0,
            currency: options?.currency ?? 'BRL',
            transaction_id: options?.transactionId ?? '',
        };
        if (options?.productId) {
            extra.product_id = options.productId;
        }
        if (options?.prazo) {
            extra.prazo = options.prazo;
        }
        if (options?.planName) {
            extra.plan_name = options.planName;
        }
        return sendGoogleConversion('purchase', extra);
    }

    /** =======================
     *  META PIXEL (fbq)
     *  ======================= */
    function trackMetaInitiateCheckout() {
        if (!hasMetaPixelId) {
            console.debug('⚠️ Meta Pixel ID não configurado para InitiateCheckout');
            return;
        }
        if (typeof (window as any).fbq !== 'function') {
            console.debug('⚠️ fbq não disponível para InitiateCheckout');
            return;
        }

        try {
            (window as any).fbq('track', 'InitiateCheckout');
            console.debug('✅ Meta Pixel: InitiateCheckout enviado');
        } catch (err) {
            console.debug('❌ Meta Pixel InitiateCheckout falhou', err);
        }
    }

    function trackMetaPurchase(options?: PurchaseOptions) {
        if (!hasMetaPixelId) {
            console.debug('⚠️ Meta Pixel ID não configurado para Purchase');
            return;
        }
        if (typeof (window as any).fbq !== 'function') {
            console.debug('⚠️ fbq não disponível para Purchase');
            return;
        }

        try {
            (window as any).fbq('track', 'Purchase', {
                value: options?.value ?? 0.0,
                currency: options?.currency ?? 'BRL',
            });
            console.debug('✅ Meta Pixel: Purchase enviado', options);
        } catch (err) {
            console.debug('❌ Meta Pixel Purchase falhou', err);
        }
    }

    /** =======================
     *  EVENTOS UNIFICADOS
     *  ======================= */
    function trackInitiateCheckout(url?: string) {
        console.debug('🎯 Tracking: InitiateCheckout');
        trackMetaInitiateCheckout();
        return trackGoogleInitiateCheckout(url);
    }

    function trackPurchase(options?: PurchaseOptions) {
        console.debug('🎯 Tracking: Purchase', options);
        trackMetaPurchase(options);
        return trackGooglePurchase(options);
    }

    /** =======================
     *  EVENTOS DO BOT
     *  ======================= */
    const botHandlers = new Map<string, BotEventHandler>();

    function normalizeEventId(eventId: unknown): string {
        if (eventId == null) {
            return '';
        }
        if (typeof eventId === 'object') {
            const id =
                (eventId as any).id ||
                (eventId as any).codigo ||
                (eventId as any).value ||
                (eventId as any).eventId ||
                '';
            return String(id).trim();
        }
        return String(eventId).trim();
    }

    function onBotEvent(eventId: unknown, handler: BotEventHandler): boolean {
        const key = normalizeEventId(eventId);
        if (!key) {
            return false;
        }
        if (typeof handler === 'function') {
            botHandlers.set(key, handler);
            console.debug('🤖 Bot event registrado:', key);
            return true;
        }
        botHandlers.delete(key);
        return false;
    }

    function offBotEvent(eventId: unknown): boolean {
        const key = normalizeEventId(eventId);
        if (!key) {
            return false;
        }
        const deleted = botHandlers.delete(key);
        if (deleted) {
            console.debug('🤖 Bot event removido:', key);
        }
        return deleted;
    }

    function handleBotEvent(eventId: unknown, payload?: any): boolean {
        const key = normalizeEventId(eventId);
        if (!botHandlers.has(key)) {
            return false;
        }
        try {
            console.debug('🤖 Bot event disparado:', key);
            botHandlers.get(key)?.(payload, key);
            return true;
        } catch (err) {
            console.debug('❌ Erro ao tratar evento do bot', key, err);
            return false;
        }
    }

    /** =======================
     *  EXPORTA OBJETO GLOBAL
     *  ======================= */
    const Tracking: TrackingAPI = {
        trackInitiateCheckout,
        trackPurchase,
        trackGoogleInitiateCheckout,
        trackGooglePurchase,
        trackMetaInitiateCheckout,
        trackMetaPurchase,
        handleBotEvent,
        onBotEvent,
        offBotEvent,
        __initialized: true,
    };

    (window as any).Tracking = Tracking;
    console.debug('✅ Tracking Service inicializado');
})();

// Export padrão (permite import direto)
export const Tracking = (typeof window !== 'undefined' ? (window as any).Tracking : undefined) as
    | TrackingAPI
    | undefined;

export default Tracking;
